import React, { useState, useEffect, useMemo } from 'react';
import { useTable } from "react-table";

// date 
import moment from 'moment';

// custom component 
import { CustomSpinner, Card } from "_atoms";
import { TableValue, TableHeader } from "_molecules";

// tremor
import { DateRangePicker, Title, Subtitle, Flex } from "@tremor/react";

// bootstrap
import { Col, Row, Button } from 'react-bootstrap';

// redux
import { useSelector, useDispatch } from "react-redux";
import { selectFirebaseInfo } from '_redux/slices/main-screen/user';
import { selectGetPreAdvisedAndWalkupsStatus, selectPreAdvisedAndWalkups, resetGetPreAdvisedAndWalkupsStatus, getPreAdvisedAndWalkups, setPreAdvisedAndWalkupsReportData } from '_redux/slices/timeline/timeline-data';
import { selectAirportBase64Logo } from '_redux/slices/analytics/reviews';

// csv
import { makeExcel } from "_utils/makeExcel";

// styles
import { Colors } from '_styles';

// constants 
const preAdvisedAndNonPreAdvised = [
    "        ",
    "Pax Type",
    "Bookings",
    "Completed",
    "Percentage"
];

const Table = ({ columns, data, title, description }) => {
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
        useTable({ columns, data });

    return (
        <Card>
            {/* heading */}
            <Row>
                <Col>
                    <Flex>
                        {/* title */}
                        <div>
                            <Title>{title}</Title>
                            {description && <Subtitle>{description}</Subtitle>}
                        </div>
                    </Flex>
                </Col>
            </Row>

            {/* table contents */}
            {Array.isFullArray(data) ? (
                <table {...getTableProps()} style={{ width: "100%", marginLeft: 12, marginRight: 12 }} >
                    {/* column headers */}
                    <thead>
                        {headerGroups.map((headerGroup, headerGroupIndex) => (
                            <tr {...headerGroup.getHeaderGroupProps()} key={headerGroupIndex} >
                                {headerGroup.headers.map((column, headerIndex) => (

                                    <th style={{ borderBottomWidth: "1px", borderBottomColor: "#d1d1d1", borderBottomStyle: "solid", width: "20%" }} {...column.getHeaderProps()} key={headerIndex}>
                                        <p>{column?.Header?.props?.title ?? "N/A"}</p>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>

                    {/* body */}
                    <tbody {...getTableBodyProps()}>
                        {rows.map((row, i) => {
                            prepareRow(row);
                            return (
                                <tr key={i} {...row.getRowProps()} style={{ borderBottomWidth: "1px", borderTopWidth: "1px", borderBottomColor: "#d1d1d1", borderTopColor: "#d1d1d1", borderBottomStyle: "solid", height: 50, fontSize: 12, cursor: "pointer" }}>
                                    {row.cells.map((cell, cellIndex) => {
                                        // Render the first column as a <th> for row headers
                                        if (cellIndex === 0) {
                                            return (
                                                <th key={cellIndex} style={{ padding: 12, textAlign: "left" }} {...cell.getCellProps()}>
                                                    <p style={{ margin: "0px", fontSize: "14px" }}>{cell?.value ?? "N/A"}</p>
                                                </th>
                                            );
                                        }
                                        // Render other columns as <td>
                                        return (
                                            <td key={cellIndex} style={{ padding: 12 }} {...cell.getCellProps()}>
                                                <p style={{ margin: "0px", fontSize: "18px", textAlign: "center" }}>{cell?.value ?? "N/A"}</p>
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            ) : (
                <Row style={{ opacity: 0.9 }} className="d-flex justify-content-center">
                    <p className="mt-3" style={{ textAlign: "center", color: Colors.PRM_LIGHT_BLUE }}>
                        {"No Analytics Found."}
                    </p>
                </Row>
            )}
        </Card>
    );
};
const TimelineAnalyticsTable = (props) => {

    //#region redux
    const dispatch = useDispatch();
    const dbUser = useSelector(selectFirebaseInfo); // logged in user
    const getPreAdvisedAndWalkupsStatus = useSelector(selectGetPreAdvisedAndWalkupsStatus);
    const preAdvisedAndWalkups = useSelector(selectPreAdvisedAndWalkups);
    const base64Logo = useSelector(selectAirportBase64Logo);


    //#endregion

    //#region states
    const [date, setDate] = useState([new Date(), new Date()]);
    const [analyticsData, setAnalyticsData] = useState([]);

    //#endregion

    //#region useEffect

    useEffect(() => {
        dispatch(resetGetPreAdvisedAndWalkupsStatus());
        handleGetPreAdvisedAndWalkups();
    }, []);

    useEffect(() => {
        const generateAnalytics = (paxType, preAdvised, advanced, walkups, nonPreAdvised) => {
            const isJER = dbUser.details.authority === "JER";
            return [
                {
                    rowHeader: "Pre-Advised",
                    paxType,
                    all: preAdvised?.all ?? 0,
                    completed: preAdvised?.completed ?? 0,
                    percentage: calculatePercentage(preAdvised?.completed, preAdvised?.all)
                },
                {
                    rowHeader: "Advanced",
                    paxType,
                    all: advanced?.all ?? 0,
                    completed: advanced?.completed ?? 0,
                    percentage: calculatePercentage(advanced?.completed, advanced?.all)
                },
                {
                    rowHeader: isJER ? "Ad Hoc" : "Walk up",
                    paxType,
                    all: walkups?.all ?? 0,
                    completed: walkups?.completed ?? 0,
                    percentage: calculatePercentage(walkups?.completed, walkups?.all)
                },
                {
                    rowHeader: "Total",
                    paxType,
                    all: (preAdvised?.all + nonPreAdvised?.all) ?? 0,
                    completed: (preAdvised?.completed + nonPreAdvised?.completed) ?? 0,
                    percentage: calculatePercentage(
                        preAdvised?.completed + nonPreAdvised?.completed,
                        preAdvised?.all + nonPreAdvised?.all
                    )
                }
            ];
        };

        const generateTotalsAnalytics = (paxType, preAdvisedDep, nonPreAdvisedDep, advancedDep, walkUpsDep, preAdvisedArr, nonPreAdvisedArr, advancedArr, walkUpsArr) => {
            const isJER = dbUser.details.authority === "JER";
            return [
                {
                    rowHeader: "Pre-Advised",
                    paxType,
                    all: `${preAdvisedDep?.all + preAdvisedArr?.all} (${calculatePercentage(preAdvisedDep?.all, preAdvisedDep?.all + preAdvisedArr?.all)} departing)`,
                    completed: preAdvisedDep?.completed + preAdvisedArr?.completed,
                    percentage: calculatePercentage(preAdvisedDep?.completed + preAdvisedArr?.completed, preAdvisedDep?.all + preAdvisedArr?.all)
                },
                {
                    rowHeader: "Advanced",
                    paxType,
                    all: `${advancedDep?.all + advancedArr?.all} (${calculatePercentage(advancedDep?.all, advancedDep?.all + advancedArr?.all)} departing)`,
                    completed: advancedDep?.completed + advancedArr?.completed,
                    percentage: calculatePercentage(advancedDep?.completed + advancedArr?.completed, advancedDep?.all + advancedArr?.all)
                },
                {
                    rowHeader: isJER ? "Ad Hoc" : "Walk up",
                    paxType,
                    all: `${walkUpsDep?.all + walkUpsArr?.all} (${calculatePercentage(walkUpsDep?.all, walkUpsDep?.all + walkUpsArr?.all)} departing)`,
                    completed: walkUpsDep?.completed + walkUpsArr?.completed,
                    percentage: calculatePercentage(walkUpsDep?.completed + walkUpsArr?.completed, walkUpsDep?.all + walkUpsArr?.all)
                },
                {
                    rowHeader: "Total",
                    paxType,
                    all: `${preAdvisedDep?.all + preAdvisedArr?.all + nonPreAdvisedDep?.all + nonPreAdvisedArr?.all} (${calculatePercentage((preAdvisedDep?.all + nonPreAdvisedDep?.all), (preAdvisedDep?.all + preAdvisedArr?.all + nonPreAdvisedDep?.all + nonPreAdvisedArr?.all))} departing)`,
                    completed: preAdvisedDep?.completed + preAdvisedArr?.completed + nonPreAdvisedDep?.completed + nonPreAdvisedArr?.completed,
                    percentage: calculatePercentage((preAdvisedDep?.completed + preAdvisedArr?.completed + nonPreAdvisedDep?.completed + nonPreAdvisedArr?.completed), (preAdvisedDep?.all + preAdvisedArr?.all + nonPreAdvisedDep?.all + nonPreAdvisedArr?.all))
                }
            ];
        };

        const totals = generateTotalsAnalytics(
            "Departures/Arrivals",
            preAdvisedAndWalkups?.departuresPreAdvised,
            preAdvisedAndWalkups?.departuresNonPreAdvised,
            preAdvisedAndWalkups?.departureAdvanced,
            preAdvisedAndWalkups?.departureWalkUps,
            preAdvisedAndWalkups?.arrivalsPreAdvised,
            preAdvisedAndWalkups?.arrivalsNonPreAdvised,
            preAdvisedAndWalkups?.arrivalsAdvanced,
            preAdvisedAndWalkups?.arrivalsWalkUps,
        )

        const departures = generateAnalytics(
            "Departures",
            preAdvisedAndWalkups?.departuresPreAdvised,
            preAdvisedAndWalkups?.departureAdvanced,
            preAdvisedAndWalkups?.departureWalkUps,
            preAdvisedAndWalkups?.departuresNonPreAdvised,

        );

        const arrivals = generateAnalytics(
            "Arrivals",
            preAdvisedAndWalkups?.arrivalsPreAdvised,
            preAdvisedAndWalkups?.arrivalsAdvanced,
            preAdvisedAndWalkups?.arrivalsWalkUps,
            preAdvisedAndWalkups?.arrivalsNonPreAdvised,

        );
        if (!Object.isObjectEmpty(preAdvisedAndWalkups)) {
            setAnalyticsData([...totals, ...departures, ...arrivals]);
            dispatch(setPreAdvisedAndWalkupsReportData([...totals, ...departures, ...arrivals]))
        } else {
            setAnalyticsData([]);
            dispatch(setPreAdvisedAndWalkupsReportData([]))

        };
    }, [preAdvisedAndWalkups]);

    //#endregion

    //#region functions

    const handleGetPreAdvisedAndWalkups = () => {
        if (props?.toTotalReports !== true) {
            const startDate = moment(new Date(date[0])).format().split("T")[0];
            const endDate = moment(new Date(date[1] ? date[1] : date[0])).format().split("T")[0];

            const payload = {
                iata: dbUser.details.authority,
                start: startDate,
                end: endDate
            }
            dispatch(getPreAdvisedAndWalkups(payload))
        }
    };

    // Function to calculate percentage
    function calculatePercentage(completed, all) {
        if (all === 0) {
            return `0%`; // avoid division by zero
        }
        const percentage = Math.round(((completed / all) * 100) * 100) / 100;
        return `${percentage}%`;

    };

    const handleExcel = async () => {
        const startDate = moment(new Date(date[0])).format().split("T")[0];
        const endDate = moment(new Date(date[1] ? date[1] : date[0])).format().split("T")[0];
        let data = {};
        data.fileName = `pre-Advised, Advanced And Walk up ${startDate}-${endDate}`;
        data.workSheetName = [`pre-Advised, Advanced And Walk up ${startDate}-${endDate}`];
        data.columnTitles = [preAdvisedAndNonPreAdvised];
        data.columnWidth = [preAdvisedAndNonPreAdvised?.map((row) => row.length + 10)];
        let excelData = []
        if (Array.isFullArray(analyticsData)) {
            analyticsData.forEach((item) => {
                let dataOBJ = {
                    rowHeader: item?.rowHeader,
                    paxType: item?.paxType,
                    all: item?.all,
                    completed: item?.completed,
                    percentage: item?.percentage,
                };
                excelData.push(dataOBJ);
            });
        }
        data.rows = [excelData];
        await makeExcel(data, dbUser.details.authority, base64Logo);
    };


    let columns = useMemo(() => [
        {
            Header: <TableHeader title={""} />, // empty header for the row header column
            accessor: "rowHeader", // accessor for the row header data
            Cell: ({ cell: { value } }) => (
                <div>
                    <TableValue title={value} comment={false} />
                </div>
            ),
        },
        {
            Header: <TableHeader title={"PAX Type"} />, // empty header for the row header column
            accessor: "paxType", // accessor for the row header data
            Cell: ({ cell: { value } }) => (
                <div>
                    <TableValue title={value} comment={false} />
                </div>
            ),
        },
        {
            Header: <TableHeader title={"Bookings"} />,
            accessor: "all",
            // render custom object
            Cell: ({ cell: { value } }) => (
                <div>
                    <TableValue title={value} comment={false} />
                </div>
            ),
        },
        {
            Header: <TableHeader title={"Completed"} />,
            accessor: "completed",
            // render custom object
            Cell: ({ cell: { value } }) => (
                <div>
                    <TableValue title={value} comment={false} />
                </div>
            ),
        },
        {
            Header: <TableHeader title={"Percentage"} />,
            accessor: "percentage",
            // render custom object
            Cell: ({ cell: { value } }) => (
                <div>
                    <TableValue title={value} comment={false} />
                </div>
            ),
        },

    ]);

    //#endregion
    return (
        <Row>
            <Col xs={12}>
                {props?.toTotalReports !== true && <><Title>{"Pre-Advised, Advanced And Walk up"}</Title>
                    <Subtitle>{"Show Pre-Advised, Advanced and Walk up bookings and completed analytics"}</Subtitle>
                    <Col xs={12} md={8} lg={8} className="mt-2 d-flex gap-2">
                        <DateRangePicker
                            onValueChange={(value) => setDate(value)}
                            value={date}
                            enableDropdown={true}
                            maxWidth="max-w-lg"
                            dropdownPlaceholder={"Today"}
                            minDate={new Date().setMonth(new Date().getMonth() - 12)} // set the minimum selectable date to 12 months ago 
                        />
                        <Button onClick={handleGetPreAdvisedAndWalkups} variant={"outline-primary"} style={{ minWidth: "127px" }}>
                            {getPreAdvisedAndWalkupsStatus === "loading" ? <CustomSpinner /> : "Get Report"}
                        </Button>
                        <Button onClick={handleExcel} variant={"outline-primary"} style={{ minWidth: "127px" }}>
                            Export Report
                        </Button>

                    </Col>
                </>}

                {Array.isFullArray(analyticsData) && <Row className="mt-4">
                    <Table
                        title={"Departures, Arrivals Analytics"}
                        data={Array.isFullArray(analyticsData) ? analyticsData : []}
                        columns={columns}
                    />
                </Row>}
            </Col>


        </Row>
    )
}

export default TimelineAnalyticsTable
